/**
 * Analytics client that sends engagement events to our backend API
 * instead of Mixpanel. Buffers events and flushes them in batches.
 *
 * Exposes the same interface as the Mixpanel SDK so that all 94+
 * consumer files continue to work without any changes.
 */

const API_ENDPOINT = import.meta.env.VITE_API_ENDPOINT;

const FLUSH_INTERVAL_MS = 5_000;
const FLUSH_BATCH_SIZE = 20;

interface PlatformInfo {
  type: "mobile" | "desktop";
  os?: string;
  browser?: string;
}

function detectPlatform(): PlatformInfo {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const isMobile = /Mobi|Android/i.test(ua);

  let os: string | undefined;
  if (/iPhone|iPad|iPod/i.test(ua)) os = "iOS";
  else if (/Android/i.test(ua)) os = "Android";
  else if (/Windows/i.test(ua)) os = "Windows";
  else if (/Mac OS X|Macintosh/i.test(ua)) os = "macOS";
  else if (/Linux/i.test(ua)) os = "Linux";

  let browser: string | undefined;
  if (/Edg\//i.test(ua)) browser = "Edge";
  else if (/Chrome/i.test(ua)) browser = "Chrome";
  else if (/Safari/i.test(ua) && !/Chrome/i.test(ua)) browser = "Safari";
  else if (/Firefox/i.test(ua)) browser = "Firefox";

  return { type: isMobile ? "mobile" : "desktop", os, browser };
}

interface QueuedEvent {
  eventName: string;
  properties?: Record<string, unknown>;
  platform?: PlatformInfo;
  gitsha?: string;
  sessionId?: string;
  timestamp: string;
}

function generateSessionId(): string {
  // Simple random ID — crypto.randomUUID() is available in modern browsers
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export class AnalyticsClient {
  private buffer: QueuedEvent[] = [];
  private superProperties: Record<string, unknown> = {};
  private userId: string | null = null;
  private optedOut = false;
  private sessionId: string;
  private platformInfo: PlatformInfo;
  private flushTimer: ReturnType<typeof setInterval> | null = null;

  constructor() {
    this.sessionId = generateSessionId();
    this.platformInfo = detectPlatform();

    this.flushTimer = setInterval(() => this.flush(), FLUSH_INTERVAL_MS);

    // Flush remaining events when the user leaves the page
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") {
          this.flush(true);
        }
      });
    }
  }

  // ------- Public API (mirrors Mixpanel interface) -------

  init(_token: string, _opts?: object): void {
    // No-op — initialization is handled in the constructor
  }

  identify(userId: string): void {
    this.userId = userId;
  }

  alias(_id: string): void {
    // No-op — we don't need aliasing with our own backend
  }

  track(eventName: string, properties?: Record<string, unknown>): void {
    if (this.optedOut) return;

    const farmGuid = this.superProperties.farm_id as string | undefined;
    const mergedProperties = {
      ...properties,
      ...(farmGuid ? { farmGuid } : {}),
    };

    const event: QueuedEvent = {
      eventName,
      properties:
        Object.keys(mergedProperties).length > 0 ? mergedProperties : undefined,
      platform: this.platformInfo,
      gitsha: this.superProperties.gitsha as string | undefined,
      sessionId: this.sessionId,
      timestamp: new Date().toISOString(),
    };

    this.buffer.push(event);

    if (this.buffer.length >= FLUSH_BATCH_SIZE) {
      this.flush();
    }
  }

  time_event(_eventName: string): void {
    // No-op — not needed for backend analytics
  }

  register(properties: Record<string, unknown>): void {
    Object.assign(this.superProperties, properties);
  }

  people = {
    set: (): void => {},
    set_once: (): void => {},
    unset: (): void => {},
    increment: (): void => {},
    remove: (): void => {},
    append: (): void => {},
    union: (): void => {},
    track_charge: (): void => {},
    clear_charges: (): void => {},
    delete_user: (): void => {},
  };

  reset(): void {
    this.userId = null;
    this.superProperties = {};
    this.sessionId = generateSessionId();
  }

  opt_out_tracking(): void {
    this.optedOut = true;
    this.buffer = [];
  }

  opt_in_tracking(): void {
    this.optedOut = false;
  }

  // ------- Internal -------

  private flush(useBeacon = false): void {
    if (this.optedOut || this.buffer.length === 0) return;

    const events = this.buffer.splice(0);
    const body = JSON.stringify({ events });

    const url = `${API_ENDPOINT}/v1/engagement-events`;

    if (useBeacon && typeof navigator !== "undefined" && navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(url, blob);
      return;
    }

    const token = window.localStorage.getItem("jwt") || "";

    // Fire-and-forget — analytics should never break the app
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body,
    }).catch(() => {
      // Silently swallow errors — same as Mixpanel behaviour
    });
  }
}

export const analyticsClient = new AnalyticsClient();
